import React, { useEffect, useState } from 'react';
import { DatePicker, Input, InputNumber, Modal, Select } from 'antd';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import {
  CLIENT_REQUEST_TYPES,
  buildClientRequestsArchiveRequest,
  parseArchiveApiBody,
} from './claimsArchiveHelpers';

const SCOPE_OPTIONS = [
  { label: 'All eligible in queue', value: 'all' },
  { label: 'By month', value: 'month' },
  { label: 'By facility (claim queries)', value: 'facility' },
];

export default function ArchiveClientRequestsModal({
  open,
  onClose,
  onSuccess,
  mode = 'archive',
  defaultTypes,
  defaultScope = 'all',
}) {
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState(defaultScope);
  const [types, setTypes] = useState(defaultTypes || CLIENT_REQUEST_TYPES);
  const [month, setMonth] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [reason, setReason] = useState('');
  const isArchive = mode !== 'unarchive';

  useEffect(() => {
    if (!open) return;
    setScope(defaultScope);
    setTypes(defaultTypes || CLIENT_REQUEST_TYPES);
    setMonth(null);
    setFacilityId(null);
    setReason('');
  }, [open, defaultScope, defaultTypes]);

  const handleConfirm = async () => {
    if (scope === 'month' && !month) {
      Modal.warning({ title: 'Month required', content: 'Select a month for this archive scope.' });
      return;
    }
    if (scope === 'facility' && (facilityId == null || facilityId === '')) {
      Modal.warning({ title: 'Facility required', content: 'Enter a facility ID for facility-scoped archive.' });
      return;
    }

    setLoading(true);
    try {
      const request = buildClientRequestsArchiveRequest({
        scope,
        month: (scope === 'month' || scope === 'facility') && month ? month.format('YYYY-MM') : undefined,
        facilityId: scope === 'facility' ? Number(facilityId) : undefined,
        types,
        reason: reason.trim() || undefined,
      });
      const resp = isArchive
        ? await lib.archiveClientRequests(request)
        : await lib.unarchiveClientRequests(request);
      const parsed = parseArchiveApiBody(resp);
      if (!parsed.ok) {
        Modal.error({
          title: isArchive ? 'Archive failed' : 'Unarchive failed',
          content: parsed.message || 'Request failed',
        });
        return;
      }
      onSuccess?.(parsed);
      onClose?.();
    } catch (e) {
      Modal.error({
        title: isArchive ? 'Archive failed' : 'Unarchive failed',
        content: e?.message || 'Request failed',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isArchive ? 'Archive client requests' : 'Unarchive client requests'}
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={isArchive ? 'Archive' : 'Unarchive'}
      okButtonProps={{ danger: isArchive, loading }}
      destroyOnClose
    >
      <p style={{ marginTop: 0 }}>
        {isArchive
          ? 'Hide completed claim queries and pending verification items from active queues.'
          : 'Restore archived client requests to the verification queue.'}
      </p>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
        Scope
      </label>
      <Select
        style={{ width: '100%', marginBottom: 12 }}
        value={scope}
        onChange={setScope}
        options={SCOPE_OPTIONS}
      />
      {scope === 'month' || scope === 'facility' ? (
        <DatePicker
          picker="month"
          style={{ width: '100%', marginBottom: 12 }}
          value={month}
          onChange={setMonth}
          placeholder={scope === 'facility' ? 'Encounter month (optional)' : 'Select month'}
        />
      ) : null}
      {scope === 'facility' ? (
        <>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
            Facility ID
          </label>
          <InputNumber
            style={{ width: '100%', marginBottom: 12 }}
            min={1}
            value={facilityId}
            onChange={setFacilityId}
            placeholder="Facility ID"
          />
        </>
      ) : null}
      <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
        Types
      </label>
      <Select
        mode="multiple"
        style={{ width: '100%', marginBottom: 12 }}
        value={types}
        onChange={setTypes}
        options={[
          { label: 'Claim queries', value: 'claim_query' },
          { label: 'Client verification', value: 'client_verification' },
        ]}
      />
      <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
        Reason (optional)
      </label>
      <Input.TextArea
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Cleared after monthly review"
      />
    </Modal>
  );
}
